import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Handle scroll for navbar
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      alert('Message must be 500 characters or less');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5967513kamldd15657g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const faqs = [
    {
      question: "What is a VPN and why do I need it?",
      answer: "A VPN (Virtual Private Network) creates a secure, encrypted connection between your device and the internet. It protects your online privacy, secures your data from hackers, and allows you to browse anonymously."
    },
    {
      question: "Will a VPN slow down my internet connection?",
      answer: "Our VPN service is optimized for speed with high-performance servers worldwide. While encryption may cause minimal speed reduction, most users experience no noticeable difference in their browsing experience."
    },
    {
      question: "How many devices can I connect simultaneously?",
      answer: "You can connect unlimited devices with a single account. Our VPN works seamlessly across all platforms including Windows, Mac, iOS, Android, and Linux."
    },
    {
      question: "Do you keep logs of my online activity?",
      answer: "No. We have a strict no-logs policy. We do not track, collect, or store any of your online activities. Your privacy is our top priority and we are committed to keeping your data completely private."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel your subscription at any time. We offer flexible plans with no long-term commitments, giving you complete control over your service."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/434b5989-a14b-4ef6-b6c2-faa57df5cd08.png" 
                alt="SUL NAUTICA LTDA Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className={`text-xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                SUL NAUTICA
              </span>
            </div>
            <div className="hidden md:flex items-center gap-10">
              <a href="#home" className={`text-sm font-light transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Home</a>
              <a href="#about" className={`text-sm font-light transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>About</a>
              <a href="#services" className={`text-sm font-light transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Services</a>
              <a href="#contact" className={`text-sm font-light transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Contact</a>
            </div>
            <a 
              href="#contact" 
              className={`px-6 py-2 rounded-full border-2 text-sm font-medium transition-all whitespace-nowrap hover:bg-teal-500 hover:border-teal-500 hover:text-white ${isScrolled ? 'border-teal-500 text-teal-500' : 'border-white text-white'}`}
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 border border-teal-400 rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 border border-teal-400 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-128 h-128 border border-teal-400 rounded-full"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-2xl">
            <h1 className="text-7xl font-bold text-white leading-tight mb-16">
              Secure Your<br />
              Digital Life<br />
              With Privacy
            </h1>
            <p className="text-lg text-white/70 mb-12 leading-relaxed max-w-xl">
              Protect your online presence with military-grade encryption. Browse anonymously, access content freely, and keep your data safe from prying eyes.
            </p>
            <a 
              href="#contact" 
              className="inline-flex items-center gap-3 px-12 py-4 bg-teal-500 text-white rounded-full text-lg font-medium hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Start Free Trial
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>

        {/* Shield Icon */}
        <div className="absolute bottom-20 right-20 opacity-20">
          <i className="ri-shield-check-line text-9xl text-teal-400"></i>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-20 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-100 text-teal-600 rounded-full text-sm font-medium mb-6">
                WHO WE ARE
              </span>
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
                Protecting Your<br />Online Privacy
              </h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  SUL NAUTICA LTDA is a leading technology company based in Porto Alegre, Brazil, dedicated to providing cutting-edge digital security solutions. We specialize in developing applications that protect your online privacy and enhance your digital experience.
                </p>
                <p>
                  Our flagship VPN service combines advanced encryption technology with user-friendly design, ensuring that everyone can enjoy a secure and private internet connection. We believe that online privacy is a fundamental right, not a luxury.
                </p>
                <p>
                  With servers strategically located around the globe, we provide fast, reliable, and secure connections that allow you to browse the internet freely and safely, no matter where you are.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/65230dd07a86b5c66b05ed9fc7de42c8.jpg" 
                  alt="VPN Security Illustration" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-teal-500 rounded-full opacity-20 blur-3xl"></div>
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-600 rounded-full text-sm font-medium mb-4">
              OUR SERVICES
            </span>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">What We Offer</h2>
            <p className="text-lg text-gray-600">Comprehensive VPN solutions for complete online protection</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-2xl transition-all duration-300 text-center group">
              <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform">
                <i className="ri-speed-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Fast Connection</h3>
              <p className="text-gray-600 leading-relaxed">
                Experience lightning-fast speeds with our optimized servers. Stream, download, and browse without any lag or buffering.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-2xl transition-all duration-300 text-center group">
              <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform">
                <i className="ri-global-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Global Servers</h3>
              <p className="text-gray-600 leading-relaxed">
                Access content from anywhere with our worldwide network of secure servers. Connect to any location instantly.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-2xl transition-all duration-300 text-center group">
              <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform">
                <i className="ri-lock-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Military Encryption</h3>
              <p className="text-gray-600 leading-relaxed">
                Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-600 rounded-full text-sm font-medium mb-4">
              FREQUENTLY ASKED
            </span>
            <h2 className="text-5xl font-bold text-gray-900">Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="border border-gray-200 rounded-2xl overflow-hidden transition-all duration-300 hover:border-teal-500"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-bold text-gray-900 pr-4">{faq.question}</span>
                  <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-2xl text-teal-500 flex-shrink-0 transition-transform`}></i>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="px-8 pb-6 pt-2 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-0 bg-white">
        <div className="grid md:grid-cols-2">
          {/* Left Panel */}
          <div className="bg-slate-900 px-12 py-20">
            <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-6">
              GET IN TOUCH
            </span>
            <h2 className="text-5xl font-bold text-white mb-12">Contact Us</h2>
            
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-teal-400"></i>
                </div>
                <div>
                  <p className="text-white text-lg">info@sulnauticaltda.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-teal-400"></i>
                </div>
                <div>
                  <p className="text-white text-lg">+1 9892877402</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-teal-400"></i>
                </div>
                <div>
                  <p className="text-white text-lg leading-relaxed">
                    Av. WENCESLAU ESCOBAR 862<br />
                    PORTO ALEGRE, Brazil<br />
                    91900-001
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Contact Form */}
          <div className="bg-white px-12 py-20">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  required
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-500 transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  required
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-500 transition-colors"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows={6}
                  maxLength={500}
                  required
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-500 transition-colors resize-none"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-8 py-4 bg-teal-500 text-white rounded-xl text-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
              {formStatus === 'success' && (
                <p className="text-green-600 text-center">Message sent successfully!</p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-600 text-center">Failed to send message. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section id="terms" className="py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 mb-12">Terms of Service</h2>
          
          <div className="space-y-8 text-gray-600 leading-relaxed">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h3>
              <p>
                By accessing and using SUL NAUTICA LTDA's VPN service, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use our service.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">2. Use of Service</h3>
              <p>
                Our VPN service is provided for lawful purposes only. You agree not to use the service for any illegal activities, including but not limited to copyright infringement, hacking, or distribution of malware. We reserve the right to terminate service for any user violating these terms.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">3. Privacy Policy</h3>
              <p>
                We maintain a strict no-logs policy. We do not track, collect, or store any information about your online activities. Your privacy is our priority, and we are committed to protecting your personal data in accordance with applicable privacy laws.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">4. Service Availability</h3>
              <p>
                While we strive to provide uninterrupted service, we do not guarantee that our VPN service will be available at all times. We reserve the right to modify, suspend, or discontinue the service at any time without prior notice.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">5. Limitation of Liability</h3>
              <p>
                SUL NAUTICA LTDA shall not be liable for any direct, indirect, incidental, special, or consequential damages resulting from the use or inability to use our service. This includes but is not limited to data loss, profit loss, or business interruption.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">6. Modifications to Terms</h3>
              <p>
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of the service after any changes constitutes acceptance of the new terms.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">7. Contact Information</h3>
              <p>
                If you have any questions about these Terms of Service, please contact us at info@sulnauticaltda.com or call us at +1 9892877402.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/434b5989-a14b-4ef6-b6c2-faa57df5cd08.png" 
                  alt="SUL NAUTICA LTDA Logo" 
                  className="h-8 w-8 object-contain"
                />
                <span className="text-2xl font-bold">SUL NAUTICA LTDA</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-8">
                Your trusted partner for online privacy and security. Protecting your digital life with cutting-edge VPN technology.
              </p>
              <div className="flex gap-3">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-teal-500 text-sm"
                />
                <button className="px-6 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  Subscribe
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-6">Quick Links</h3>
              <div className="space-y-4">
                <a href="#home" className="block text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Home</a>
                <a href="#about" className="block text-white/60 hover:text-white transition-colors text-sm cursor-pointer">About</a>
                <a href="#services" className="block text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Services</a>
                <a href="#faq" className="block text-white/60 hover:text-white transition-colors text-sm cursor-pointer">FAQ</a>
                <a href="#contact" className="block text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Contact</a>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-lg font-bold mb-6">Legal</h3>
              <div className="space-y-4">
                <a href="#terms" className="block text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Terms of Service</a>
                <Link to="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="block text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Website Builder</Link>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm">© 2025 SUL NAUTICA LTDA. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
              <a href="#" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                <i className="ri-linkedin-line text-xl"></i>
              </a>
              <a href="#" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                <i className="ri-facebook-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
